/** Move existing controls, never clone their state or event handlers. */
export class ResponsiveSettings {
  readonly button = document.createElement('button');
  private readonly dialog = document.createElement('dialog');
  private readonly content = document.createElement('div');
  private readonly media = window.matchMedia('(max-width: 1024px)');
  private readonly anchors: Comment[];
  private readonly sync = (): void => {
    this.close();
    this.nodes.forEach((node, index) => {
      if (this.media.matches) this.content.append(node);
      else this.anchors[index]!.after(node);
    });
    this.button.hidden = !this.media.matches;
  };

  constructor(private readonly nodes: HTMLElement[], host: HTMLElement, onClose?: () => void, onTitle?: () => void) {
    if (!document.getElementById('mandalizm-responsive-settings-style')) {
      const style = document.createElement('style');
      style.id = 'mandalizm-responsive-settings-style';
      style.textContent = `
        .mandalizm-advanced-toggle { font: inherit; color:#cdfaf3; background:#082b33;
          border:1px solid #377b80; border-radius:6px; padding:10px 14px; min-height:44px; cursor:pointer; }
        .mandalizm-advanced-toggle[hidden] { display:none; }
        .mandalizm-advanced-sheet { box-sizing:border-box; width:min(640px,100%); max-width:100%;
          max-height:92dvh; margin:auto auto 0; padding:16px max(16px,env(safe-area-inset-right))
          max(16px,env(safe-area-inset-bottom)) max(16px,env(safe-area-inset-left));
          color:#cdfaf3; background:#03141d; border:1px solid #377b80; border-radius:14px 14px 0 0;
          font:12px/1.5 ui-monospace,monospace; overflow:auto; overscroll-behavior:contain; }
        .mandalizm-advanced-sheet::backdrop { background:#000b; }
        .mandalizm-advanced-sheet__close { position:sticky; top:0; display:block; margin:0 0 16px auto; z-index:2; }
        .mandalizm-advanced-sheet .mandalizm-visual__body { max-height:none; }
        @media(max-width:1024px) {
          .mandalizm-select { padding:12px; font-size:12px; }
          .mandalizm-select__top { min-width:0; gap:10px; padding-left:66px; }
          .mandalizm-select__storage { max-width:50%; font-size:9px; }
          .mandalizm-select__body { grid-template-columns:minmax(0,1fr); grid-template-rows:minmax(70px,1fr) auto;
            gap:8px; margin:8px 0; }
          .mandalizm-select__detail { min-width:0; overflow:visible; }
          .mandalizm-select__name, .mandalizm-select__song b, .mandalizm-select__song small {
            display:block; min-width:0; max-width:100%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .mandalizm-select__name { font-size:15px; margin-bottom:8px; }
          .mandalizm-select .mandalizm-picker__difficulty { min-width:0; min-height:44px; }
          .mandalizm-select .mandalizm-picker__difficulties { margin-bottom:8px; }
          .mandalizm-select__play { min-height:44px; padding:10px; margin-bottom:6px; }
          .mandalizm-select__bottom { margin-top:6px; padding-top:6px; gap:6px; }
          .mandalizm-select__action { min-height:44px; padding:8px; font-size:10px; }
          .mandalizm-select__status { margin-left:0; max-width:100%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:10px; }
          .mandalizm-create__inner { width:calc(100% - 24px); max-height:calc(100dvh - 24px); }
          .mandalizm-create__title { margin-bottom:8px; font-size:18px; }
          .mandalizm-create__field { margin-bottom:8px; min-width:0; }
          .mandalizm-create__chosen { min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .mandalizm-create__slider { grid-template-columns:minmax(80px,1fr) minmax(60px,1.2fr) 45px; gap:6px; }
          .mandalizm-create__slider input, .mandalizm-create input { min-width:0; max-width:100%; }
          .mandalizm-create__detected-row { grid-template-columns:auto minmax(60px,1fr); }
          .mandalizm-advanced-sheet * { box-sizing:border-box; }
          .mandalizm-song-info { top:10px; right:10px; width:min(260px,48vw); padding:6px 8px; }
          .mandalizm-song-info__title { font-size:11px; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .mandalizm-song-info__cover, .mandalizm-song-info__author, .mandalizm-song-info__rows,
          .mandalizm-song-info__note { display:none; }
          .mandalizm-song-info__bar { margin-top:4px; }
          .mandalizm-judge { left:10px; bottom:64px; min-width:0; width:180px; padding:6px 8px;
            display:grid; grid-template-columns:1fr 1fr; align-items:baseline; gap:0 8px; }
          .mandalizm-judge[hidden] { display:none; }
          .mandalizm-judge__score-label, .mandalizm-judge__combo-label, .mandalizm-judge__counts { display:none; }
          .mandalizm-judge__score, .mandalizm-judge__combo { font-size:18px; }
          .mandalizm-judge__grade { font-size:11px; margin:0; padding:0; border:0; }
          .mandalizm-judge__timing { font-size:9px; overflow-wrap:anywhere; }
          .mandalizm-settings { max-width:calc(100vw - 24px); max-height:calc(100dvh - 80px); overflow:auto; }
          .mandalizm-settings-toggle { min-height:44px; bottom:max(10px,env(safe-area-inset-bottom)); }
          body:not(:has(.mandalizm-settings:not([hidden]))):has(.mandalizm-select:not([hidden]):not(.mandalizm-select--leaving)) > .mandalizm-settings-toggle,
          body:not(:has(.mandalizm-settings:not([hidden]))):has(.mandalizm-title:not([hidden]):not(.mandalizm-title--leaving)) > .mandalizm-settings-toggle,
          body:not(:has(.mandalizm-settings:not([hidden]))):has(.mandalizm-create:not([hidden])) > .mandalizm-settings-toggle { display:none; }
          .mandalizm-dock { width:auto!important; max-width:140px; }
          .mandalizm-dock:has(.mandalizm-advanced-sheet .mandalizm-visual[hidden]):has(.mandalizm-advanced-sheet .mandalizm-export[hidden]) { display:none!important; }
        }
        @media(max-width:1024px) and (max-height:500px) {
          .mandalizm-select__body { grid-template-columns:minmax(0,1fr) minmax(0,1fr); grid-template-rows:minmax(0,1fr); }
          .mandalizm-select__detail { overflow:auto; }
        }
      `;
      // Outrank component defaults regardless of lazy stylesheet injection order.
      style.textContent = style.textContent.replace(/(^|[},\n])(\s*)(\.mandalizm-)/g, '$1$2:root $3');
      document.head.append(style);
    }
    this.anchors = nodes.map(node => {
      const anchor = document.createComment('desktop settings position');
      node.before(anchor);
      return anchor;
    });
    this.button.type = 'button';
    this.button.className = 'mandalizm-advanced-toggle';
    this.button.textContent = 'ADVANCED';
    this.button.setAttribute('aria-haspopup', 'dialog');
    this.button.setAttribute('aria-expanded', 'false');
    this.dialog.className = 'mandalizm-advanced-sheet';
    this.dialog.setAttribute('aria-label', 'Advanced settings');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'mandalizm-advanced-toggle mandalizm-advanced-sheet__close';
    close.textContent = 'CLOSE SETTINGS';
    close.addEventListener('click', () => this.close());
    this.dialog.append(close, this.content);
    if (onTitle) {
      const title = document.createElement('button');
      title.type = 'button'; title.className = 'mandalizm-advanced-toggle'; title.textContent = 'TITLE';
      title.addEventListener('click', onTitle);
      this.dialog.prepend(title);
    }
    host.append(this.button, this.dialog);
    this.button.addEventListener('click', () => {
      this.dialog.showModal();
      this.button.setAttribute('aria-expanded', 'true');
      close.focus();
    });
    this.dialog.addEventListener('close', () => {
      this.button.setAttribute('aria-expanded', 'false');
      onClose?.();
    });
    // Keep Escape local: the game's shortcut would otherwise leave Song Select
    // or close CREATE SONG behind an open modal. Native cancel closes the sheet.
    this.dialog.addEventListener('keydown', event => { if (event.key === 'Escape') event.stopPropagation(); });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(); });
    this.media.addEventListener('change', this.sync);
    this.sync();
  }

  close(): void {
    if (this.dialog.open) this.dialog.close();
    this.button.setAttribute('aria-expanded', 'false');
  }

  dispose(): void {
    this.media.removeEventListener('change', this.sync);
    this.nodes.forEach((node, index) => this.anchors[index]!.replaceWith(node));
    this.dialog.remove();
    this.button.remove();
  }
}
